import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { AlertCircle, BadgeCheck } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { Portal } from '@radix-ui/react-portal';

import { useGradeFlow } from '@/states/grade-flow';
import { useUserProfile } from '@/states/user-profile';
import { extractBaseImageName } from '@/utils/cases';

import { Overlay } from '@/components/ui/overlay';
import { ImageSegDataAccordionSelect } from '@/components/insights/image-seg-data-accordion-select';

import { LeftSideData } from './left-side-data';

type GradeReadableImagesProps = {
  enableError?: boolean;
  size?: 'sm' | 'md';
  filter?: 'all' | 'left' | 'right';
  displayTitle?: boolean;
};

const SIZE_MAP_STYLE = {
  sm: 'xl:h-24',
  md: 'xl:h-40',
};

export function ReadableImages(props: GradeReadableImagesProps) {
  const { enableError, displayTitle, filter = 'all', size = 'md' } = props;
  const [expanded, setExpanded] = useState<string | undefined>();
  const [segmentationSelected, setSegmentationSelected] = useState<
    ('vessels' | 'optic_disc' | 'lesions')[] | undefined
  >();
  const { t } = useTranslation();

  const { profile } = useUserProfile();
  const isVitaziAdmin = profile?.role === 'vitaziadmin';

  const { images, readableImages, isSaving, layerImages, isLoading, segmentationData } =
    useGradeFlow(
      useShallow((s) => ({
        isSaving: s.isSavingGrade,
        images: s.images,
        readableImages: s.form.images_readable,
        layerImages: s.layerImages,
        isLoading: s.isLoadingLayerImages || s.isLoadingImageSegmentationData,
        segmentationData: s.imageSegmentationData,
      }))
    );

  const expandedLayerImages = useMemo(() => {
    if (!layerImages?.length || !expanded) return undefined;

    const expandedName = extractBaseImageName(expanded);
    const filteredLayers = layerImages.filter((i) =>
      extractBaseImageName(i.layer).includes(expandedName)
    );

    const vesselsLayer = filteredLayers.find((i) => i.layer.includes('vessels'));
    const opticDiscLayer = filteredLayers.find((i) => i.layer.includes('optic_disc'));
    const lesionsLayer = filteredLayers.find((i) => i.layer.includes('lesions'));

    return {
      vessels: vesselsLayer,
      optic_disc: opticDiscLayer,
      lesions: lesionsLayer,
    };
  }, [layerImages, expanded]);

  const imagesFiltered = useMemo(() => {
    if (filter === 'all') {
      return images ?? [];
    }

    return images?.filter((i) => i.lat === filter) ?? [];
  }, [images, filter]);

  const handleClose = useCallback(() => {
    setExpanded(undefined);
  }, []);

  const handleExpand = useCallback(
    (image: string) => {
      if (isSaving) return;

      setExpanded(image);
    },
    [isSaving]
  );

  if (!imagesFiltered?.length) {
    return <p className="pb-4 text-gray-500 text-sm">{t('grade-no-image-available')}</p>;
  }

  const ImagesThumbnail = imagesFiltered.map((i, index) => {
    const image = readableImages?.find((state) => state.img === i.image);

    if (!image) return null;

    const isImageUndefined = image?.is_readable === '';
    const isImageReadable = image?.is_readable === 'yes';

    let color = 'text-gray-200';

    if (isImageReadable) {
      color = 'text-green-500 dark:text-green-400';
    } else if (isImageUndefined) {
      color = 'text-gray-200';
    } else {
      color = 'text-yellow-500 dark:text-yellow-400';
    }

    if (isImageUndefined && enableError) {
      color = 'text-red-500';
    }

    return (
      <div key={`grade-image-${index}`} className="cursor-pointer">
        <img
          className={`${SIZE_MAP_STYLE[size]} object-scale-down pointer`}
          src={i.url}
          alt={`Case img ${index + 1}`}
          onClick={() => handleExpand(image.img)}
        />
        <div className="py-1 bg-card-foreground dark:bg-background">
          {image ? (
            <div className={`text-xs text-center font-medium ${color}`}>
              {isImageUndefined ? (
                t('unanswered')
              ) : isImageReadable ? (
                <div className="flex items-center justify-center">
                  <span className="mr-1">{t('readable')}</span>
                  <BadgeCheck className="h-4 w-4" />
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span className="mr-1">{t('unreadable')}</span>
                  <AlertCircle className="h-4 w-4" />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  });

  return (
    <>
      {displayTitle ? (
        <h2 className="text-base/7 font-semibold text-gray-900 text-center mb-4">
          {t('grade-question-set-image-quality')}
        </h2>
      ) : null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 overflow-x-auto">{ImagesThumbnail}</div>
      {expanded ? (
        <Portal asChild>
          <Overlay open={expanded !== undefined} onClose={handleClose} allowScroll>
            <div className="flex flex-wrap lg:flex-row justify-between items-start py-6 px-4 gap-4">
              <LeftSideData
                img={expanded}
                setExpanded={(img) => setExpanded(img)}
                onCloseModal={handleClose}
              />
              <div className="flex-1 h-auto flex flex-col items-center w-full shrink-0 min-w-[80vw] xl:min-w-0 order-1 xl:order-2">
                <picture className="relative">
                  <img
                    className="h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] relative"
                    src={imagesFiltered.find((i) => i.image === expanded)?.url}
                    alt={`Expanded image ${expanded}`}
                  />
                  {/* display overlay img by segmentation selected */}
                  {segmentationSelected?.includes('vessels') && expandedLayerImages?.vessels ? (
                    <img
                      className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                      src={expandedLayerImages?.vessels.url}
                      alt="Vascular overlay"
                    />
                  ) : null}
                  {segmentationSelected?.includes('optic_disc') &&
                  expandedLayerImages?.optic_disc ? (
                    <img
                      className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                      src={expandedLayerImages.optic_disc.url}
                      alt="Optic disc overlay"
                    />
                  ) : null}
                  {segmentationSelected?.includes('lesions') && expandedLayerImages?.lesions ? (
                    <img
                      className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                      src={expandedLayerImages.lesions.url}
                      alt="Lesions overlay"
                    />
                  ) : null}
                </picture>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">{ImagesThumbnail}</div>
              </div>
              {isVitaziAdmin ? (
                <ImageSegDataAccordionSelect
                  img={expanded}
                  setSegmentationSelected={setSegmentationSelected}
                  segmentationSelected={segmentationSelected}
                  layerImages={expandedLayerImages}
                  loading={isLoading}
                  segmentationData={segmentationData}
                />
              ) : null}
            </div>
          </Overlay>
        </Portal>
      ) : null}
    </>
  );
}
